import React, { useState, useCallback, useEffect } from 'react';
import { Routes, Route, Outlet, BrowserRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { orgService, authService } from '../Services';

import Layout from './Common/Layouts/Layout';
import Login from './Auth/Login';
import ResetPassword from './Auth/ResetPassword';
import SignUp from './Auth/SignUp';
import ConfirmEmail from './Auth/ConfirmEmail';
import Loader from './Common/Loader';
import SuggestionBox from './SuggestionBox/Index';
import Logs from './Logs/Index';
import Log from './Logs/Log';
import Profile from './Profile/Index';
import Configuration from './Configuration/Index';
import Reports from './Reports/Index';
import Versions from './Versions/Index';
import DivePlans from './DivePlans/Index';
import Volunteer from './Volunteer/Index';
import ScheduleIndex from './Schedule/Index';
import Page from './Configuration/Pages/Page';
import ActivitiesIndex from './Activities/Index';

import { setId, setName, setLogoExt, setParentId, setRef, setModules, setVersion } from '../redux/stores/orgSlice';
import { setStatus, setPermissions } from '../redux/stores/userSlice';
import { setLocalStatus, setLocalPermissions } from '../redux/stores/localUserSlice';

import '../styles/styles.scss';

export const App = () => {
  const [loading, setLoading] = useState(true);
  const [noHost, setNoHost] = useState(false);
  const local = useSelector((state) => state.org.remember);
  const orgId = useSelector((state) => state.org.id);
  const orgName = useSelector((state) => state.org.name);
  const user = useSelector((state) => state[local ? 'localUser' : 'user'].id);
  const dispatch = useDispatch();
  const modules = useSelector((state) => state.org.modules);

  const fetchData = useCallback(async () => {
    const queryParams = new URLSearchParams(window.location.search);
    if (!orgId || (!user && orgId)) {
      setLoading(true);
      const data = await orgService.getByHost(queryParams.get('ref') || '');

      if (data.error === 'no-host')
        setNoHost(true);
      else {
        dispatch(setId(data.org.id));
        dispatch(setName(data.org.name));
        dispatch(setLogoExt(data.org.logoExt));
        dispatch(setParentId(data.org.parentId));
        dispatch(setVersion(data.version.version));
        dispatch(setModules(data.modules));
        document.title = data.org.name;

        if (data.ref)
          dispatch(setRef(data.ref));
      }
    } else if (orgId && user && queryParams.get('ref')) {
      setLoading(true);
      const dataOrg = await authService.signInRef(queryParams.get('ref'));

      if (dataOrg.org && dataOrg.org.id) {
        dispatch(setId(dataOrg.org.id));
        dispatch(setName(dataOrg.org.Name));
        dispatch(setLogoExt(dataOrg.org.LogoFileExt));
        dispatch(setModules(dataOrg.modules));
        document.title = dataOrg.org.Name;

        if (local) {
          dispatch(setLocalPermissions(dataOrg.org.permissions));
          dispatch(setLocalStatus(dataOrg.org.status));
        } else {
          dispatch(setPermissions(dataOrg.org.permissions));
          dispatch(setStatus(dataOrg.org.status));
        }

        window.history.pushState({}, null, window.location.href.split('?')[0]); 
      }
    } else {
      document.title = orgName;
    }
    setLoading(false);
  }, [dispatch, orgId, orgName, user, local]);

  useEffect(() => {
      fetchData();
  }, [fetchData])

  return (
    <>
    {loading ? <Loader /> : 
      (noHost ? 
        <div className="error-404">There's nothing here: 404!</div> :
        <BrowserRouter>
            <Routes>
                <Route element={<ProtectedRoute isAllowed={!!user} />}>
                    <Route index element={<Log />} />
                    <Route path="suggestion-box" element={<SuggestionBox />} />
                    <Route path="logs/*" element={<Logs />} />
                    <Route path="dive-plans/*" element={<DivePlans />} />
                    <Route path="reports/*" element={<Reports />} />
                    <Route path="volunteer/*" element={<Volunteer />} />
                    <Route path="configuration/*" element={<Configuration />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="versions" element={<Versions />} />
                    <Route path="schedule/*" element={<ScheduleIndex />} />
                    {modules.includes('Activities') ?
                      <Route path="activities/*" element={<ActivitiesIndex />} /> :
                      <Route path="*" element={<div className="error-404">There's nothing here: 404!</div>} />
                    }

                    {modules.includes('Pages') ?
                      <Route path=":slug" element={<Page />} /> :
                      <Route path="*" element={<div className="error-404">There's nothing here: 404!</div>} />
                    }
                </Route>
                <Route path="reset-password" element={<ResetPassword />} />
                <Route path="sign-up" element={<SignUp />} />
                <Route path="confirm-email" element={<ConfirmEmail />} />
                <Route path="*" element={<div className="error-404">There's nothing here: 404!</div>} />
            </Routes>
        </BrowserRouter>
      )
      }
    </>
  );
};

export default App;

const ProtectedRoute = ({ isAllowed, children }) => {
    if (!isAllowed) {
      return <Login />;
    } else 
      return <Layout>
        {children ? children : <Outlet />}
      </Layout>;
};